import { useState, useEffect } from "react";
import type { ReactNode } from "react";

import DropdownWithSearch from "../../atomic/DropdownWithSearch";
import { DropdownWithSearchOption } from "../../atomic/DropdownWithSearch/interface";
import type { NotifyStatesTypes } from "../../atomic/Notification";
import { LoanApplicationService } from "../../../services/LoanApplication";

interface BaseProps {
  pageName: string;
  loanApplicationID: string;
  isLenderDashboard?: boolean;
  fetchToken: () => string;
  notify: (message: {
    title: ReactNode;
    type?: NotifyStatesTypes;
    text?: ReactNode;
  }) => void;
  isMandatory?: boolean;
}

interface MoveApplicationProps extends BaseProps {
  moveApplication: true;
  onRejectionReasonChange: (reason: {
    rejectionReason: string;
    subRejectionReason: string;
  }) => void;
}

interface RegularProps extends BaseProps {
  moveApplication?: false;
  onRejectionReasonChange: (reason: string) => void;
}

type IProps = MoveApplicationProps | RegularProps;

export default function RejectionReasonSelector({
  pageName,
  loanApplicationID,
  onRejectionReasonChange,
  isLenderDashboard = false,
  fetchToken,
  notify,
  moveApplication = false,
  isMandatory = false
}: IProps) {
  const [rejectionReasonCategoryOption, setRejectionReasonCategoryOption] =
    useState<DropdownWithSearchOption>({
      name: "Rejection Reason",
      value: ""
    });

  const [
    rejectionReasonSubCategoryOption,
    setRejectionReasonSubCategoryOption
  ] = useState<DropdownWithSearchOption>({
    name: "Rejection Reason Sub Category",
    value: ""
  });

  const [rejectionReasonCategoryOptions, setRejectionReasonCategoryOptions] =
    useState<DropdownWithSearchOption[]>([]);

  const [
    rejectionReasonSubCategoryOptions,
    setRejectionReasonSubCategoryOptions
  ] = useState<DropdownWithSearchOption[]>([]);

  useEffect(() => {
    (async () => {
      const response = await LoanApplicationService.genericDropdownList({
        pageName,
        token: fetchToken(),
        isLenderDashboard,
        payload: {
          filter: [],
          resourceName: "manual_credit_review_rejection_dropdown",
          dropdownName: "default",
          loanApplicationID: loanApplicationID
        }
      });

      if (
        response.status &&
        (response?.data as Array<{ label: string; value: string }>)?.length > 0
      ) {
        const data = response?.data as Array<{ label: string; value: string }>;
        setRejectionReasonCategoryOptions(
          data.map((item) => ({
            name: item.label,
            value: item.value
          }))
        );
      } else {
        notify({
          title: "Error",
          type: "error",
          text: response?.error || "Something went wrong"
        });
      }
    })();
  }, [pageName, loanApplicationID, isLenderDashboard]);

  useEffect(() => {
    (async () => {
      if (rejectionReasonCategoryOption?.value) {
        const response = await LoanApplicationService.genericDropdownList({
          pageName,
          token: fetchToken(),
          isLenderDashboard,
          payload: {
            filter: [rejectionReasonCategoryOption?.value],
            resourceName: "manual_credit_review_rejection_dropdown",
            dropdownName: "default",
            loanApplicationID: loanApplicationID
          }
        });

        if (
          response?.status &&
          (response?.data as Array<{ label: string; value: string }>)?.length >
            0
        ) {
          const data = response?.data as Array<{
            label: string;
            value: string;
          }>;
          setRejectionReasonSubCategoryOptions(
            data.map((item) => ({
              name: item.label,
              value: item.value
            }))
          );
        } else {
          notify({
            title: "Error",
            type: "error",
            text: response?.error || "Something went wrong"
          });
        }
      }
    })();
  }, [
    rejectionReasonCategoryOption.value,
    pageName,
    loanApplicationID,
    isLenderDashboard
  ]);

  useEffect(() => {
    setRejectionReasonCategoryOption({
      name: "Rejection Reason Category",
      value: ""
    });

    setRejectionReasonSubCategoryOption({
      name: "Rejection Reason Sub Category",
      value: ""
    });
  }, []);

  return (
    <>
      {(() => {
        const categoryDropdown = (
          <DropdownWithSearch
            label="Select Rejection Reason"
            hasSearch={false}
            requiredIcon={isMandatory}
            styles={{
              container: moveApplication ? "!w-full" : "max-w-[305px]"
            }}
            options={rejectionReasonCategoryOptions}
            onChange={(
              currentRejectionReasonCategoryOption: DropdownWithSearchOption
            ) => {
              if (
                currentRejectionReasonCategoryOption?.value ===
                rejectionReasonCategoryOption?.value
              ) {
                return;
              }

              setRejectionReasonSubCategoryOption({
                name: "Rejection Reason Sub Category",
                value: ""
              });

              setRejectionReasonSubCategoryOptions([]);
              onRejectionReasonChange(
                moveApplication
                  ? { rejectionReason: "", subRejectionReason: "" }
                  : ""
              );

              setRejectionReasonCategoryOption(
                currentRejectionReasonCategoryOption
              );
            }}
            selectedItems={rejectionReasonCategoryOption}
            placeholder={"Rejection Category"}
          />
        );

        const subCategoryDropdown = (
          <DropdownWithSearch
            label="Select Sub Rejection Reason"
            hasSearch={false}
            requiredIcon={isMandatory}
            styles={{
              container: moveApplication ? "!w-full" : "max-w-[305px]"
            }}
            options={rejectionReasonSubCategoryOptions}
            onChange={(
              currentRejectionReasonSubCategoryOption: DropdownWithSearchOption
            ) => {
              if (
                currentRejectionReasonSubCategoryOption?.value ===
                rejectionReasonSubCategoryOption?.value
              ) {
                return;
              }
              setRejectionReasonSubCategoryOption(
                currentRejectionReasonSubCategoryOption
              );

              onRejectionReasonChange(
                moveApplication
                  ? {
                      rejectionReason: rejectionReasonCategoryOption?.value,
                      subRejectionReason:
                        currentRejectionReasonSubCategoryOption?.value
                    }
                  : currentRejectionReasonSubCategoryOption?.value
              );
            }}
            selectedItems={rejectionReasonSubCategoryOption}
            placeholder={"Rejection Sub Category"}
          />
        );

        if (moveApplication) {
          return (
            <div className="mt-5 flex justify-between gap-2">
              <div className="!w-1/2">{categoryDropdown}</div>
              {rejectionReasonCategoryOption?.value && (
                <div className="!w-1/2">{subCategoryDropdown}</div>
              )}
            </div>
          );
        }

        return (
          <>
            <div className="mt-5">{categoryDropdown}</div>
            {rejectionReasonCategoryOption?.value && (
              <div className="mt-5">{subCategoryDropdown}</div>
            )}
          </>
        );
      })()}
    </>
  );
}
